import React, { useCallback } from "react";
import styled from "styled-components";
import { colors, font, Button } from "@literati/public-ui-shared-react";
import { SwipeData, useSwipe } from "components/bookShuffle/useSwipe";
import {
  bookShuffleCardSharedStyles,
  bookShuffleDeckCardSizeStyles,
} from "components/bookShuffle/bookShuffleStyles";
import { BookShuffleUseSwipeCardProps } from "components/bookShuffle/bookShuffleTypes";
import { assetUrl } from "utils";
import analytics from "analytics";
import useBookShuffle from "components/bookShuffle/useBookShuffle";

/** This is the className that will be pre-pended to all of BookShuffleCardStart's inner component classNames */
export const BOOK_SHUFFLE_CARD_START_CLASS_BASE = "book-shuffle-card-start";

/** These are all of the internal classNames that are used within the BookShuffleCardStart component */
export const BookShuffleCardStartClassName = {
  BASE: BOOK_SHUFFLE_CARD_START_CLASS_BASE,
  TITLE_DESCRIPTION_CONTAINER: `${BOOK_SHUFFLE_CARD_START_CLASS_BASE}-title-description-container`,
  TITLE: `${BOOK_SHUFFLE_CARD_START_CLASS_BASE}-title`,
  DESCRIPTION: `${BOOK_SHUFFLE_CARD_START_CLASS_BASE}-description`,
  BUTTON: `${BOOK_SHUFFLE_CARD_START_CLASS_BASE}-button`,
} as const;

const CARD_PADDING = "2rem";

const CardDiv = styled.div<{ $swiping: boolean }>`
  ${bookShuffleCardSharedStyles}
  ${bookShuffleDeckCardSizeStyles}
  background-image: url(${assetUrl("/static/img/book-shuffle-card-bg.svg")});
  background-size: cover;
  position: relative;
  cursor: ${({ $swiping }) => ($swiping ? "grabbing" : "grab")};
`;

const TitleDescriptionContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - calc(${CARD_PADDING} * 2));
`;

const Title = styled.h2`
  font-family: ${font.fontFamily.hoefler};
  font-weight: 900;
  font-size: 1.375rem;
  line-height: 120%;
  text-align: center;
  color: ${colors.inkBlack};
  margin-bottom: 0.75rem;
  user-select: none;
`;

const Description = styled.p`
  font-family: ${font.fontFamily.avenir};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 150%;
  text-align: center;
  color: ${colors.warmGray3};
  user-select: none;
`;

const StyledButton = styled(Button)`
  position: absolute;
  bottom: calc(${CARD_PADDING});
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - calc(${CARD_PADDING} * 2));
`;

export type BookShuffleCardStartProps = BookShuffleUseSwipeCardProps & {
  className?: string;
};

export const BookShuffleCardStart = (props: BookShuffleCardStartProps) => {
  const { className = "" } = props;

  const {
    startTitle = "Literati Book Shuffle",
    startDescription = "Would your child read these books? Swipe through the following titles to let us know. We'll learn their preferences and take it from there!",
    startButtonText = "Start Shuffling",
    goNext,
    completeAnimation,
    setSwipeData,
    currentIndex,
  } = useBookShuffle();

  const cls = BookShuffleCardStartClassName;

  const handleAnimateCycleComplete = useCallback(
    (swipeData: SwipeData) => {
      if (currentIndex !== -1) return;
      setSwipeData({
        swipeData,
      });
    },
    [setSwipeData, currentIndex]
  );

  const { accept, ref, swiping } = useSwipe<HTMLDivElement>({
    onStatusDecision: goNext,
    onExitComplete: completeAnimation,
    onAnimateCycleComplete: handleAnimateCycleComplete,
  });

  return (
    <CardDiv
      className={`${cls.BASE} ${className}`}
      ref={ref}
      $swiping={swiping}
    >
      <TitleDescriptionContainer className={cls.TITLE_DESCRIPTION_CONTAINER}>
        <Title className={cls.TITLE}>{startTitle}</Title>
        <Description className={cls.DESCRIPTION}>
          {startDescription}
        </Description>
      </TitleDescriptionContainer>
      <StyledButton
        aria-label="start"
        onClick={() => {
          accept();
          analytics.trackAmplitudeClickEvent(startButtonText);
        }}
        className={cls.BUTTON}
      >
        {startButtonText}
      </StyledButton>
    </CardDiv>
  );
};

export default BookShuffleCardStart;
